import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle, ArrowDownToLine, ArrowUpFromLine, CalendarDays, Check, Pencil, Undo2 } from "lucide-react";
import { Button } from "@akuna/ui";
import { toast } from "@/hooks/use-toast";
import { useSettleTitle, useUnsettleTitle } from "@/hooks/useFinancial";
import { daysFrom, debtors as buildDebtors, titleBuckets } from "@/lib/financial/calc";
import type { FinancialAccount, FinancialTitle, Period, TitleKind } from "@/lib/financial/types";
import { cn } from "@/lib/utils";
import type { FormMode } from "./FinancialFormDialog";
import { defaultAccountFor, isTitleLinked, thClass } from "./helpers";
import { PaymentAgenda } from "./PaymentAgenda";
import { MethodChip, Panel, PanelTitle, PillFilter } from "./ui";
import { useFinancialFormat } from "./useFinancialFormat";

type Filter = "open" | "overdue" | "paid";

interface Props {
  kind: TitleKind;
  titles: FinancialTitle[];
  accounts: FinancialAccount[];
  period: Period;
  today: string;
  canUpdate: boolean;
  onForm: (mode: FormMode) => void;
}

export function TitlesTab({ kind, titles, accounts, period, today, canUpdate, onForm }: Props) {
  const { t } = useTranslation("financial");
  const fmt = useFinancialFormat();
  const isRec = kind === "receivable";
  const [filter, setFilter] = useState<Filter>("open");
  const [focusName, setFocusName] = useState<string | null>(null);
  const settle = useSettleTitle();
  const unsettle = useUnsettleTitle();

  const open = useMemo(() => titles.filter((x) => x.status === "open"), [titles]);
  const buckets = titleBuckets(titles, today);

  const rows = titles
    .filter((x) => x.due_date >= period.from && x.due_date <= period.to)
    .filter((x) => (filter === "paid" ? x.status === "paid" : x.status === "open" && (filter === "open" || daysFrom(today, x.due_date) < 0)))
    .sort((a, b) => a.due_date.localeCompare(b.due_date));

  const toggle = async (title: FinancialTitle) => {
    try {
      if (title.status === "paid") {
        await unsettle.mutateAsync(title.id);
        toast({ title: t("toasts.undone") });
        return;
      }
      const account = defaultAccountFor(title.payment_method, accounts);
      if (!account) {
        toast({ title: t("toasts.noAccount"), variant: "destructive" });
        return;
      }
      await settle.mutateAsync({ titleId: title.id, accountId: account.id });
      toast({ title: t(isRec ? "toasts.received" : "toasts.paid", { value: fmt.brl(Number(title.amount)) }) });
    } catch {
      toast({ title: t("toasts.error"), variant: "destructive" });
    }
  };

  const statusOf = (x: FinancialTitle) => {
    if (x.status === "paid") return { text: t(isRec ? "titlesTable.statusReceived" : "titlesTable.statusPaid"), tone: "text-state-success-foreground" };
    const d = daysFrom(today, x.due_date);
    if (d < 0) return { text: t("titlesTable.late", { count: -d }), tone: "text-state-danger-foreground" };
    if (d === 0) return { text: t("titlesTable.dueToday"), tone: "text-state-warning-foreground" };
    if (d === 1) return { text: t("titlesTable.dueTomorrow"), tone: "text-muted-foreground" };
    return { text: t("titlesTable.dueInDays", { count: d }), tone: "text-muted-foreground" };
  };

  const summary = [
    { label: t("summary.overdue"), value: buckets.overdue, Icon: AlertTriangle, tone: "text-state-danger-foreground", border: "border-state-danger/30", sub: t(isRec ? "summary.recOverdueSub" : "summary.payOverdueSub") },
    {
      label: t("summary.dueToday"), value: buckets.dueToday, Icon: CalendarDays, tone: "text-state-warning-foreground", border: "border-state-warning/50",
      sub: !buckets.dueToday ? t("summary.nothingToday") : isRec ? t("summary.recTodaySub") : t("summary.payTodaySub", { count: open.filter((x) => x.due_date === today).length }),
    },
    { label: t("summary.next7"), value: buckets.next7, Icon: isRec ? ArrowDownToLine : ArrowUpFromLine, tone: isRec ? "text-state-success-foreground" : "text-foreground/80", border: "border-border", sub: t(isRec ? "summary.recNext7Sub" : "summary.payNext7Sub") },
  ];

  // responsável em foco + quem mais deve
  const debtorList = isRec ? buildDebtors(titles, today) : [];
  const focus = debtorList.find((d) => d.name === focusName) ?? debtorList[0];

  const filterOptions = [
    { value: "open" as const, label: t(isRec ? "titlesTable.filterOpenRec" : "titlesTable.filterOpenPay") },
    { value: "overdue" as const, label: t("titlesTable.filterOverdue") },
    { value: "paid" as const, label: t(isRec ? "titlesTable.filterPaidRec" : "titlesTable.filterPaidPay") },
  ];

  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        {summary.map((s) => (
          <div key={s.label} className={cn("rounded-2xl border bg-card px-5 py-4", s.border)}>
            <div className={cn("flex items-center gap-2 text-sm font-semibold", s.tone)}>
              <s.Icon className="h-4 w-4" aria-hidden />
              {s.label}
            </div>
            <div className={cn("mt-2 font-display text-2xl font-extrabold tracking-tight", s.tone)}>{fmt.brl(s.value)}</div>
            <div className="mt-0.5 text-xs text-muted-foreground">{s.sub}</div>
          </div>
        ))}
      </div>

      {!isRec ? <PaymentAgenda open={open} today={today} onForm={onForm} /> : null}

      <div className="flex flex-col gap-4 xl:flex-row xl:items-start">
        <Panel className="min-w-0 flex-1 overflow-hidden">
          <div className="flex flex-col gap-3 border-b border-border px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
            <PillFilter options={filterOptions} value={filter} onChange={(v) => setFilter(v as Filter)} label={t("titlesTable.actions")} />
            <div className="text-sm text-muted-foreground">{t("titlesTable.count", { count: rows.length })}</div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[820px] text-sm">
              <thead>
                <tr className="bg-muted/40">
                  <th className={cn(thClass, "w-36 pl-5")}>{t("titlesTable.due")}</th>
                  <th className={thClass}>{t(isRec ? "titlesTable.client" : "titlesTable.payee")}</th>
                  <th className={thClass}>{t("titlesTable.about")}</th>
                  <th className={cn(thClass, "w-32")}>{t("titlesTable.method")}</th>
                  <th className={cn(thClass, "w-32 text-right")}>{t("titlesTable.amount")}</th>
                  <th className={cn(thClass, "w-48 pr-5")}><span className="sr-only">{t("titlesTable.actions")}</span></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((x) => {
                  const st = statusOf(x);
                  const paid = x.status === "paid";
                  return (
                    <tr key={x.id} className={cn("border-t border-border/70", paid && "bg-muted/20")}>
                      <td className="py-3 pl-5 pr-3 align-middle">
                        <div className="font-semibold">{fmt.dnum(x.due_date)}</div>
                        <div className={cn("mt-0.5 text-xs font-semibold", st.tone)}>{st.text}</div>
                      </td>
                      <td className="px-3 py-3 align-middle">
                        <div className="font-semibold">{x.counterparty_name}</div>
                        {x.counterparty_detail ? <div className="mt-0.5 text-xs text-muted-foreground">{x.counterparty_detail}</div> : null}
                      </td>
                      <td className="px-3 py-3 align-middle text-foreground/80">
                        {x.description}
                        {x.installment_count ? <span className="text-muted-foreground"> · {t("titlesTable.installment", { n: x.installment_number, total: x.installment_count })}</span> : null}
                      </td>
                      <td className="px-3 py-3 align-middle"><MethodChip>{fmt.method(x.payment_method)}</MethodChip></td>
                      <td className={cn("px-3 py-3 text-right align-middle font-display text-[15px] font-bold", paid ? "text-muted-foreground" : "text-foreground")}>{fmt.brl(Number(x.amount))}</td>
                      <td className="py-3 pl-3 pr-5 align-middle">
                        {canUpdate ? (
                          <div className="flex items-center justify-end gap-2">
                            {/* título quitado se corrige desfazendo a baixa, para não desalinhar do lançamento */}
                            {!paid && !isTitleLinked(x) ? (
                              <Button size="icon-sm" variant="outline" aria-label={t("titlesTable.edit")} onClick={() => onForm({ type: "editTitle", title: x })}>
                                <Pencil className="h-3.5 w-3.5" aria-hidden />
                              </Button>
                            ) : null}
                            <Button
                              size="sm"
                              variant={paid ? "outline" : "secondary"}
                              onClick={() => toggle(x)}
                              disabled={settle.isPending || unsettle.isPending}
                              className={cn(!paid && (isRec ? "bg-state-success-soft text-state-success-foreground" : "bg-state-danger-soft text-state-danger-foreground"))}
                            >
                              {paid ? <Undo2 className="mr-1.5 h-3.5 w-3.5" aria-hidden /> : <Check className="mr-1.5 h-3.5 w-3.5" aria-hidden />}
                              {paid ? t("titlesTable.undo") : t(isRec ? "titlesTable.received" : "titlesTable.paid")}
                            </Button>
                          </div>
                        ) : null}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {!rows.length ? <p className="px-5 py-4 text-sm text-muted-foreground">{filter === "paid" ? t("titlesTable.emptyPaid") : t("titlesTable.empty")}</p> : null}
        </Panel>

        {isRec && focus ? (
          <aside className="flex w-full flex-col gap-4 xl:w-80 xl:shrink-0">
            <Panel className="p-5">
              <div className="font-display text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">{t("focus.title")}</div>
              <div className="mt-2 font-display text-xl font-extrabold tracking-tight">{focus.name}</div>
              {focus.detail ? <div className="mt-0.5 text-xs text-muted-foreground">{focus.detail}</div> : null}
              <div className="mt-4 grid grid-cols-2 gap-2.5">
                <div className="rounded-xl bg-muted/50 p-3">
                  <div className="text-xs text-muted-foreground">{t("focus.total")}</div>
                  <div className="mt-1 font-display text-base font-extrabold">{fmt.brl(focus.total)}</div>
                </div>
                <div className="rounded-xl bg-state-danger-soft p-3 text-state-danger-foreground">
                  <div className="text-xs">{t("focus.overdue")}</div>
                  <div className="mt-1 font-display text-base font-extrabold">{fmt.brl(focus.overdue)}</div>
                </div>
              </div>
              <div className="mt-3 flex flex-col">
                {[...focus.titles].sort((a, b) => a.due_date.localeCompare(b.due_date)).map((x) => {
                  const d = daysFrom(today, x.due_date);
                  return (
                    <button
                      key={x.id}
                      type="button"
                      onClick={() => onForm({ type: "editTitle", title: x })}
                      disabled={isTitleLinked(x)}
                      className="flex items-center gap-2.5 border-t border-border/70 py-2.5 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    >
                      <span className="min-w-0 flex-1">
                        <span className="block text-sm font-medium">{x.description}</span>
                        <span className="mt-0.5 block text-xs text-muted-foreground">
                          {fmt.dnum(x.due_date)} · {d < 0 ? t("focus.rowLate", { count: -d }) : d === 0 ? t("focus.rowToday") : t("focus.rowIn", { count: d })}
                        </span>
                      </span>
                      <span className={cn("whitespace-nowrap text-sm font-bold", d < 0 ? "text-state-danger-foreground" : "text-foreground/80")}>{fmt.brl(Number(x.amount))}</span>
                    </button>
                  );
                })}
              </div>
              {canUpdate ? (
                <div className="mt-4 flex gap-2">
                  <Button
                    className="flex-1"
                    disabled={settle.isPending}
                    onClick={async () => {
                      for (const x of focus.titles) await toggle(x);
                    }}
                  >
                    {t("focus.receiveAll")}
                  </Button>
                  <Button className="flex-1" variant="outline" onClick={() => onForm({ type: "reneg", name: focus.name, titles: focus.titles })}>
                    {t("focus.renegotiate")}
                  </Button>
                </div>
              ) : null}
            </Panel>

            <Panel className="p-5">
              <div className="font-display text-base font-bold">{t("focus.topDebtors")}</div>
              <div className="mt-0.5 text-xs text-muted-foreground">{t("focus.topDebtorsSub")}</div>
              <div className="mt-2 flex flex-col gap-0.5">
                {debtorList.slice(0, 6).map((d) => (
                  <button
                    key={d.name}
                    type="button"
                    onClick={() => setFocusName(d.name)}
                    aria-pressed={d.name === focus.name}
                    className={cn(
                      "flex items-center gap-2.5 rounded-lg p-2.5 text-left hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                      d.name === focus.name && "bg-primary/10",
                    )}
                  >
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium">{d.name}</span>
                      <span className="mt-0.5 block text-xs text-muted-foreground">
                        {t("focus.debtorTitles", { count: d.count })} · {d.maxDaysLate ? t("focus.lateBy", { count: d.maxDaysLate }) : t("focus.allOnTime")}
                      </span>
                    </span>
                    <span className={cn("whitespace-nowrap font-display text-sm font-bold", d.overdue ? "text-state-danger-foreground" : "text-foreground/80")}>
                      {fmt.brl(d.overdue || d.total)}
                    </span>
                  </button>
                ))}
              </div>
            </Panel>
          </aside>
        ) : null}
      </div>
    </div>
  );
}
